import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const PREFERRED_VIDEO_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=h264',
  'video/mp4',
  'video/quicktime;codecs=h264',
  'video/quicktime',
];

const CameraCapture = ({
  onComplete,
  onCapture,
  onCancel,
  maxImages = 10,
  maxVideoDuration = 60,
  singleCapture = false,
  showPreview = true,
  autoStart = false,
  /** 'photo' | 'video' | 'both' — limits capture UI (default both for legacy callers). */
  captureMode = 'both',
  compact = false,
}) => {
  const { t } = useTranslation();
  const [capturedMedia, setCapturedMedia] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureType, setCaptureType] = useState(() => (captureMode === 'video' ? 'video' : 'photo'));
  const [videoDuration, setVideoDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoTimerRef = useRef(null);
  const allocatedObjectUrlsRef = useRef(new Set());

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: captureType === 'video'
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error(t('cameraCapture.cameraAccessDenied'));
      console.error('Camera error:', error);
    }
  }, [captureType, t]);

  const stopCamera = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      try {
        recorder.stop();
      } catch (error) {
        console.error('Error stopping active recorder:', error);
      }
    }

    if (videoTimerRef.current) {
      clearInterval(videoTimerRef.current);
      videoTimerRef.current = null;
    }
    setIsRecording(false);
    setVideoDuration(0);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const registerObjectUrl = useCallback((url) => {
    if (url?.startsWith('blob:')) {
      allocatedObjectUrlsRef.current.add(url);
    }
  }, []);

  const releaseObjectUrl = useCallback((url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
      allocatedObjectUrlsRef.current.delete(url);
    }
  }, []);

  const releaseAllObjectUrls = useCallback(() => {
    allocatedObjectUrlsRef.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    allocatedObjectUrlsRef.current.clear();
  }, []);

  const generateImagePreview = useCallback((blob) => {
    if (typeof FileReader !== 'undefined') {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => {
          const fallbackUrl = URL.createObjectURL(blob);
          registerObjectUrl(fallbackUrl);
          resolve(fallbackUrl);
        };
        reader.readAsDataURL(blob);
      });
    }

    const objectUrl = URL.createObjectURL(blob);
    registerObjectUrl(objectUrl);
    return Promise.resolve(objectUrl);
  }, [registerObjectUrl]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    context.drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        if (singleCapture) {
          releaseAllObjectUrls();
        }
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const preview = await generateImagePreview(blob);
        
        const newMedia = {
          file,
          preview,
          type: 'image',
          timestamp: new Date()
        };
        
        if (singleCapture) {
          setCapturedMedia([newMedia]);
          if (onCapture && typeof onCapture === 'function') {
            onCapture(file);
          }
        } else {
          setCapturedMedia(prev => [...prev, newMedia]);
        }
        
        toast.success(t('cameraCapture.photoCaptured'));
      }
    }, 'image/jpeg', 0.8);
  }, [singleCapture, onCapture, generateImagePreview, releaseAllObjectUrls, t]);

  const getMediaRecorderSupport = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
      return { supported: false, mimeType: null };
    }

    const { MediaRecorder } = window;

    if (typeof MediaRecorder.isTypeSupported === 'function') {
      const matchingType = PREFERRED_VIDEO_TYPES.find(type => {
        try {
          return MediaRecorder.isTypeSupported(type);
        } catch (e) {
          return false;
        }
      });

      if (matchingType) {
        return { supported: true, mimeType: matchingType };
      }
    }

    // Even if no preferred type is explicitly supported, attempting without a mimeType can still work.
    return { supported: true, mimeType: null };
  }, []);

  const startVideoRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) {
      toast.error(t('cameraCapture.cameraNotActive'));
      return;
    }

    const activeRecorder = mediaRecorderRef.current;
    if (activeRecorder && activeRecorder.state === 'recording') {
      toast.error(t('cameraCapture.recordingAlreadyInProgress'));
      return;
    }

    const { supported, mimeType } = getMediaRecorderSupport();
    if (!supported) {
      toast.error(t('cameraCapture.recordingNotSupported'));
      return;
    }

    const attemptStart = (options) => {
      let recorder;

    try {
        recorder = options ? new MediaRecorder(stream, options) : new MediaRecorder(stream);
      } catch (creationError) {
        console.error('Failed to create MediaRecorder:', creationError);
        return false;
      }
    
    const chunks = [];
    
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
      chunks.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error event:', event?.error || event);
        toast.error(t('cameraCapture.recordingStoppedDueToError'));
        if (recorder.state !== 'inactive') {
          try {
            recorder.stop();
          } catch (stopError) {
            console.error('Failed to stop recorder after error:', stopError);
          }
        }
      };

      recorder.onstop = () => {
        if (videoTimerRef.current) {
          clearInterval(videoTimerRef.current);
          videoTimerRef.current = null;
        }
        mediaRecorderRef.current = null;
        setIsRecording(false);

        if (chunks.length === 0) {
          return;
        }

        const recordedType = (chunks[0] && chunks[0].type) || recorder.mimeType || (options && options.mimeType) || 'video/webm';
        const normalizedType = recordedType || 'video/webm';
        const extension = normalizedType.includes('mp4') || normalizedType.includes('quicktime') ? 'mp4' : 'webm';
        const blob = new Blob(chunks, { type: normalizedType });
        const file = new File([blob], `video_${Date.now()}.${extension}`, { type: normalizedType });

        if (singleCapture) {
          releaseAllObjectUrls();
        }
      const preview = URL.createObjectURL(blob);
        registerObjectUrl(preview);
      
      const newMedia = {
        file,
        preview,
        type: 'video',
        timestamp: new Date()
      };
      
      if (singleCapture) {
          setCapturedMedia([newMedia]);
        if (onCapture && typeof onCapture === 'function') {
          onCapture(file);
        }
      } else {
        setCapturedMedia(prev => [...prev, newMedia]);
      }
      
      toast.success(t('cameraCapture.videoCaptured'));
    };
    
      mediaRecorderRef.current = recorder;

      try {
        recorder.start();
      } catch (startError) {
        console.error('MediaRecorder start() failed:', startError);
        mediaRecorderRef.current = null;
        try {
          recorder.stop();
        } catch (stopError) {
          // Ignore stop failure on start error
        }
        return false;
      }

    setIsRecording(true);
    setVideoDuration(0);
    
      if (videoTimerRef.current) {
        clearInterval(videoTimerRef.current);
      }

    videoTimerRef.current = setInterval(() => {
      setVideoDuration(prev => {
        if (prev >= maxVideoDuration) {
            const currentRecorder = mediaRecorderRef.current;
            if (currentRecorder && currentRecorder.state === 'recording') {
              try {
                currentRecorder.stop();
              } catch (stopError) {
                console.error('Failed to stop recorder at max duration:', stopError);
              }
            }
          return maxVideoDuration;
        }
        return prev + 1;
      });
    }, 1000);

      return true;
    };

    const primaryOptions = mimeType ? { mimeType } : undefined;
    if (attemptStart(primaryOptions)) {
      return;
    }

    if (mimeType) {
      console.warn('Retrying video recording without explicit mimeType');
      if (attemptStart(undefined)) {
        toast(t('common.recordingStarted'), { icon: '🎥' });
        return;
      }
    }

    toast.error(t('cameraCapture.unableToStartRecording'));
  }, [getMediaRecorderSupport, maxVideoDuration, onCapture, registerObjectUrl, releaseAllObjectUrls, singleCapture, t]);

  const stopVideoRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setIsRecording(false);
      return;
    }

    if (recorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }

    try {
      recorder.stop();
    } catch (error) {
      console.error('Failed to stop recorder:', error);
    }

      if (videoTimerRef.current) {
        clearInterval(videoTimerRef.current);
      videoTimerRef.current = null;
    }
    setIsRecording(false);
    setVideoDuration(0);
  }, []);

  const removeMedia = (index) => {
    setCapturedMedia(prev => {
      const newMedia = [...prev];
      const [removed] = newMedia.splice(index, 1);
      if (removed?.preview) {
        releaseObjectUrl(removed.preview);
      }
      return newMedia;
    });
  };

  const handleComplete = () => {
    if (capturedMedia.length === 0) {
      toast.error(t('cameraCapture.captureAtLeastOne'));
      return;
    }
    
    const files = capturedMedia.map(media => media.file);
    const callback = onComplete || onCapture;
    if (callback && typeof callback === 'function') {
      callback(files);
    }
  };

  const handleStartCapture = async () => {
    setIsCapturing(true);
    await startCamera();
  };

  const handleStopCapture = () => {
    stopCamera();
    setIsCapturing(false);
  };

  React.useEffect(() => {
    if (captureMode === 'photo') {
      setCaptureType('photo');
    } else if (captureMode === 'video') {
      setCaptureType('video');
    }
  }, [captureMode]);

  React.useEffect(() => {
    if (!autoStart) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setIsCapturing(true);
      if (!cancelled) {
        await startCamera();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [autoStart, startCamera]);

  React.useEffect(() => {
    return () => {
      stopCamera();
      releaseAllObjectUrls();
    };
  }, [stopCamera, releaseAllObjectUrls]);

  React.useEffect(() => {
    if (!isCapturing) {
      return undefined;
    }

    const stream = streamRef.current;
    const wantsAudio = captureType === 'video';
    const hasAudio = !!(stream && stream.getAudioTracks && stream.getAudioTracks().length > 0);

    if (wantsAudio && !hasAudio) {
      let cancelled = false;
      (async () => {
        stopCamera();
        if (!cancelled) {
          await startCamera();
        }
      })();
      return () => {
        cancelled = true;
    };
    }

    if (!wantsAudio && hasAudio && stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
    }

    return undefined;
  }, [captureType, isCapturing, startCamera, stopCamera]);

  const showModeToggle = captureMode === 'both';

  return (
    <div className={compact ? '' : 'bg-landspot-gray px-4 py-6'}>
      <div className={compact ? 'w-full' : 'max-w-md mx-auto'}>
        <div className={compact ? 'space-y-4' : 'bg-white rounded-lg p-6 space-y-6'}>
          {!compact && (
            <h2 className="text-2xl font-bold text-center mb-6 font-geist">
              {t('cameraCapture.title')}
            </h2>
          )}

          {showModeToggle && (
            <div className="flex space-x-4 mb-6">
            <button
              type="button"
              onClick={() => setCaptureType('photo')}
              className={`flex-1 py-2 px-4 rounded-lg font-geist ${
                captureType === 'photo' ? 'btn-active' : 'btn-inactive'
              }`}
            >
              {t('cameraCapture.photo')}
            </button>
            <button
              onClick={() => setCaptureType('video')}
              className={`flex-1 py-2 px-4 rounded-lg font-geist ${
                captureType === 'video' ? 'btn-active' : 'btn-inactive'
              }`}
            >
              {t('cameraCapture.video')}
            </button>
            </div>
          )}

          {/* Camera View */}
          {isCapturing && (
            <div className="mb-6">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full aspect-video object-cover"
                />
                {captureType === 'video' && isRecording && (
                  <div className="absolute top-4 left-4 bg-red-600 text-white px-2 py-1 rounded text-sm font-geist">
                    {t('cameraCapture.rec', { seconds: videoDuration })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Capture Controls */}
          <div className="text-center">
            {!isCapturing && !autoStart ? (
              <button
                type="button"
                onClick={handleStartCapture}
                className="btn-primary text-lg px-6 py-3 font-geist"
              >
                {t('cameraCapture.startCamera')}
              </button>
            ) : isCapturing ? (
              <div className="space-y-4">
                {captureType === 'photo' ? (
                  <button
                    onClick={capturePhoto}
                    className="btn-primary text-lg px-6 py-3 font-geist"
                  >
                    {t('cameraCapture.capturePhoto')}
                  </button>
                ) : (
                  <div className="space-y-2">
                    {!isRecording ? (
                      <button
                        onClick={startVideoRecording}
                        className="btn-primary text-lg px-6 py-3 font-geist"
                      >
                        {t('cameraCapture.startRecording')}
                      </button>
                    ) : (
                      <button
                        onClick={stopVideoRecording}
                        className="btn-secondary text-lg px-6 py-3 font-geist"
                      >
                        {t('cameraCapture.stopRecording')}
                      </button>
                    )}
                  </div>
                )}
                {!autoStart && (
                  <button
                    type="button"
                    onClick={handleStopCapture}
                    className="btn-outline text-lg px-6 py-3 font-geist"
                  >
                    {t('cameraCapture.stopCamera')}
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {/* Captured Media Preview */}
          {showPreview && capturedMedia.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 font-geist">
                {t('cameraCapture.capturedMedia', { current: capturedMedia.length, max: maxImages })}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {capturedMedia.map((media, index) => (
                  <div key={index} className="relative">
                    {media.type === 'image' ? (
                      <img
                        src={media.preview}
                        alt={t('cameraCapture.capturedAlt', { number: index + 1 })}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ) : (
                      <video
                        src={media.preview}
                        className="w-full h-32 object-cover rounded-lg"
                        controls
                      />
                    )}
                    {!singleCapture && (
                    <button
                      onClick={() => removeMedia(index)}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-geist"
                    >
                      ×
                    </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!singleCapture && (
            <div className="flex space-x-4 pt-2">
              <button
                onClick={onCancel}
                className="flex-1 btn-outline font-geist"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleComplete}
                disabled={capturedMedia.length === 0}
                className="flex-1 btn-primary font-geist disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
